import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Check, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

interface BrandingSettings {
  logo: string;
  name: string;
  favicon: string;
}

export default function AdminBrandingPanel() {
  const [settings, setSettings] = useState<BrandingSettings>({
    logo: '/logo.svg',
    name: 'MXPatfan',
    favicon: '/favicon.svg',
  });

  const [formData, setFormData] = useState<BrandingSettings>(settings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    fetchBrandingSettings();
  }, []);

  const fetchBrandingSettings = async () => {
    try {
      const response = await fetch(`\${API_BASE_URL}/api/admin/settings/branding`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch branding settings');

      const data = await response.json();
      setSettings(data);
      setFormData(data);
    } catch (error) {
      console.error('Error fetching branding settings:', error);
      setMessage({ type: 'error', text: 'Failed to load branding settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof BrandingSettings, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`\${API_BASE_URL}/api/admin/settings/branding`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to update branding settings');

      const data = await response.json();
      setSettings(formData);
      setMessage({ type: 'success', text: 'Branding settings updated successfully!' });

      // Clear message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving branding settings:', error);
      setMessage({ type: 'error', text: 'Failed to save branding settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(settings);
    setMessage(null);
  };

  const isModified =
    JSON.stringify(settings) !== JSON.stringify(formData);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black">
        <div className="text-white">Loading branding settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gradient-to-r from-black via-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link
            to="/admin"
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition"
          >
            <ChevronLeft size={20} />
            Back to Admin
          </Link>
          <h1 className="text-4xl font-bold">Platform Branding</h1>
          <p className="text-gray-400 mt-2">Customize your platform logo, name, and favicon</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Message */}
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                : 'bg-red-500/10 text-red-400 border border-red-500/30'
            }`}
          >
            {message.type === 'success' ? (
              <Check size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            {message.text}
          </motion.div>
        )}

        {/* Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Platform Name */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
            <label className="block text-sm font-semibold text-gray-300 mb-3">
              Platform Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., MXPatfan"
              className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500"
            />
            <p className="text-sm text-gray-500 mt-2">
              This name will appear in your header and page titles
            </p>
          </div>

          {/* Logo URL */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
            <label className="block text-sm font-semibold text-gray-300 mb-3">
              Logo URL
            </label>
            <input
              type="text"
              value={formData.logo}
              onChange={(e) => handleInputChange('logo', e.target.value)}
              placeholder="e.g., /logo.svg or https://example.com/logo.svg"
              className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500"
            />
            <p className="text-sm text-gray-500 mt-2">
              Recommended: SVG or PNG format, 200x50px or similar aspect ratio
            </p>
            {formData.logo && (
              <div className="mt-4 p-4 bg-black rounded border border-gray-700">
                <p className="text-sm text-gray-400 mb-2">Preview:</p>
                <div className="bg-gray-900 p-4 rounded flex items-center">
                  <img
                    src={formData.logo}
                    alt="Logo preview"
                    className="h-12 object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/logo.svg';
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Favicon URL */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
            <label className="block text-sm font-semibold text-gray-300 mb-3">
              Favicon URL
            </label>
            <input
              type="text"
              value={formData.favicon}
              onChange={(e) => handleInputChange('favicon', e.target.value)}
              placeholder="e.g., /favicon.svg or https://example.com/favicon.ico"
              className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500"
            />
            <p className="text-sm text-gray-500 mt-2">
              Recommended: SVG or ICO format, 32x32px or 64x64px
            </p>
          </div>

          {/* Current Settings Preview */}
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Current Settings</h3>
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-gray-400">Platform Name:</p>
                <p className="text-white font-mono">{settings.name}</p>
              </div>
              <div>
                <p className="text-gray-400">Logo URL:</p>
                <p className="text-white font-mono text-xs break-all">{settings.logo}</p>
              </div>
              <div>
                <p className="text-gray-400">Favicon URL:</p>
                <p className="text-white font-mono text-xs break-all">{settings.favicon}</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6 border-t border-gray-800">
            <button
              onClick={handleSave}
              disabled={!isModified || saving}
              className="flex-1 px-6 py-3 rounded-lg font-semibold text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={
                !isModified || saving
                  ? { background: '#666' }
                  : { background: 'linear-gradient(135deg, #FF6B35 0%, #F968A6 100%)' }
              }
            >
              {saving ? 'Saving...' : 'Save Branding Changes'}
            </button>
            <button
              onClick={handleReset}
              disabled={!isModified || saving}
              className="flex-1 px-6 py-3 rounded-lg font-semibold text-white border border-gray-700 hover:border-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
